import { CreateUnregisteredFromForeclosureDialog } from "@/components/license-details/cards/create-unregistered-from-foreclosure-dialog";
import { useForeclosureDetailsDialogSize } from "@/components/license-details/cards/foreclosure-details-dialog-size";
import { ForeclosureLicenseDetailsDialog } from "@/components/license-details/cards/foreclosure-license-details-dialog";
import {
  FORECLOSURE_DETAIL_TILE_CLASS,
  ForeclosureOrdinanceSummarySection,
} from "@/components/license-details/cards/foreclosure-ordinance-summary-section";
import { LiveLinkButton } from "@/components/license-details/cards/live-link-action";
import { PartyRoleInfo } from "@/components/license-details/party-role-info";
import { ForeclosureCaseNumberEditCell } from "@/components/non_registrant/foreclosure-case-number-edit-cell";
import { ForeclosureCasePartyEditCell } from "@/components/non_registrant/foreclosure-case-party-edit-cell";
import { ForeclosureCaseReviewStatusBadge } from "@/components/non_registrant/foreclosure-case-review-status-badge";
import { ForeclosureCaseStartDateEditCell } from "@/components/non_registrant/foreclosure-case-start-date-edit-cell";
import { HistoryActorRow } from "@/components/shared/history-actor-row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ErrorComponent from "@/components/ui/error";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { getAttomForeclosureCaseDetails } from "@/hera-api/attom-foreclosure-case-api";
import {
  getGovernmentJurisdictionOptions,
  getGovernmentLicenseTypeConfigurationsOptions,
  getJurisdictionsOptions,
  getLicenseTypeQuestionsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import {
  type ForeclosureLicenseCandidate,
  type ForeclosureUnregisteredMatch,
  getForeclosureCaseReviewHistory,
  getForeclosureLicenseMatches,
  getForeclosureUnregisteredMatch,
  linkForeclosureCaseToLicense,
  linkForeclosureCaseToUnregistered,
  suggestForeclosureCaseParties,
  suggestForeclosureCaseReview,
  updateForeclosureCaseReview,
} from "@/hera-api/foreclosure-license-link-api";
import type { GovernmentForeclosureCaseListItem } from "@/hera-api/government-foreclosure-cases-api";
import {
  formatAttomFieldLabel,
  formatAttomFieldValue,
  partitionCaseSummaryEntries,
} from "@/lib/attom-foreclosure-case";
import {
  applyForeclosureCaseReviewToCachedLists,
  invalidateForeclosureCaseChartQueries,
  invalidateForeclosureCaseListRowQueries,
} from "@/lib/foreclosure-case-query-invalidation";
import {
  FORECLOSURE_CASE_REVIEW_STATUS_LABELS,
  FORECLOSURE_CASE_REVIEW_STATUS_OPTIONS,
  type ForeclosureCaseReviewStatus,
} from "@/lib/foreclosure-case-review-status";
import {
  buildForeclosureOrdinanceSummaries,
  registrationRequirementQuestions,
} from "@/lib/foreclosure-ordinance-summary";
import { registrantDisplayName } from "@/lib/registrant-display";
import {
  cn,
  getQueryErrorMessage,
  getRegistrationStatusDisplay,
} from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

const DOCUMENT_COLUMNS = [
  "source",
  "record_type",
  "document_date",
  "instrument_number",
  "book_page",
  "borrower_name_owner",
  "lender_name",
  "trustee_name",
  "default_amount",
  "auction_date",
  "record_last_updated",
] as const;

interface AttomForeclosureDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseNumber: string;
  jurisdictionFips?: string | null;
  caseNumberUrlTemplate?: string | null;
  caseNumberCopyToClipboard?: boolean;
  caseNumberHttpMethod?: string | null;
  caseNumberValueExtractRegex?: string | null;
  caseNumberRequestFields?: Record<string, string> | null;
  caseNumberBrowserHelperConfig?: Record<string, unknown> | null;
  caseNumberBlankAnswerUrl?: string | null;
  /** Display case number when an operator override replaces the ATTOM value. */
  effectiveCaseNumber?: string | null;
  isCaseNumberOverridden?: boolean;
  /** ATTOM-loaded start date. The details pane edits the review override. */
  systemStartDate?: string | null;
  effectiveStartDate?: string | null;
  isStartDateOverridden?: boolean;
  /** When set, shows/links HERA foreclosure registrations for this case. */
  portal?: "government" | "operator";
  jurisdictionId?: string | null;
  countyFips?: string | null;
  caseApn?: string | null;
  propertyAddressFull?: string | null;
  propertyAddressCity?: string | null;
  propertyAddressState?: string | null;
  propertyAddressZip?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Indexed case fields shown immediately; Athena may replace with a fuller case. */
  caseSummary?: Record<string, string | number | null> | null;
  reviewStatus?: ForeclosureCaseReviewStatus | null;
  reviewComment?: string | null;
  reviewUpdatedAt?: string | null;
  reviewUpdatedByDisplay?: string | null;
  suggestedReviewStatus?: ForeclosureCaseReviewStatus | null;
  suggestedReviewComment?: string | null;
  omtPartyId?: string | null;
  omtDisplay?: string | null;
  amsPartyId?: string | null;
  amsDisplay?: string | null;
  registrantAccountId?: string | null;
  registrantDisplay?: string | null;
  isOmtOverridden?: boolean;
  isAmsOverridden?: boolean;
  isRegistrantOverridden?: boolean;
  unregisteredPropertiesPath?:
    | "/government/unregistered-properties"
    | "/operator/unregistered-properties";
  onLinked?: () => void;
  onNext?: () => void;
  hasNext?: boolean;
  onPrevious?: () => void;
  hasPrevious?: boolean;
  /** Nest above an elevated parent (e.g. license details opened from case review). */
  elevated?: boolean;
}

function formatPropertyLine(candidate: ForeclosureLicenseCandidate): string {
  const street = [candidate.property_address1, candidate.property_address2]
    .filter(Boolean)
    .join(", ");
  const cityStateZip = [
    candidate.property_city,
    candidate.property_state,
    candidate.property_zipcode,
  ]
    .filter(Boolean)
    .join(", ");
  return [street, cityStateZip].filter(Boolean).join(" · ") || "—";
}

function formatMatchReasons(reasons: string[]): string {
  return reasons
    .map((reason) => {
      if (reason === "apn") return "APN";
      if (reason === "address") return "Address";
      if (reason === "case_number") return "Case number";
      return reason;
    })
    .join(", ");
}

function formatLicensePartyDisplay(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "—";
}

function CaseSummaryField({
  fieldKey,
  value,
}: {
  fieldKey: string;
  value: string | number | null;
}) {
  return (
    <div className="min-w-0">
      <dt className="break-words text-xs font-medium text-muted-foreground">
        {formatAttomFieldLabel(fieldKey)}
      </dt>
      <dd className="break-words text-sm [overflow-wrap:anywhere]">
        {formatAttomFieldValue(value)}
      </dd>
    </div>
  );
}

function LicenseDetailsCard({
  candidate,
  onOpen,
}: {
  candidate: ForeclosureLicenseCandidate;
  onOpen: () => void;
}) {
  const statusDisplay = getRegistrationStatusDisplay(candidate.status);

  return (
    <button
      type="button"
      aria-label={`Open registration ${candidate.license_display_id}`}
      className="grid w-full cursor-pointer gap-3 rounded-md border p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:grid-cols-2 lg:grid-cols-3"
      onClick={onOpen}
    >
      <span>
        <span className="block text-xs font-medium text-muted-foreground">
          License
        </span>
        <span className="block text-sm">
          <span className="text-primary underline-offset-4 hover:underline">
            {candidate.license_display_id}
          </span>
          {candidate.is_deleted ? (
            <span className="ml-2 text-xs text-muted-foreground">
              (inactive)
            </span>
          ) : null}
        </span>
      </span>
      <span>
        <span className="block text-xs font-medium text-muted-foreground">
          Status
        </span>
        <span className="block text-sm">
          <Badge variant={statusDisplay.variant}>{statusDisplay.label}</Badge>
        </span>
      </span>
      <span>
        <span className="block text-xs font-medium text-muted-foreground">
          Effective date
        </span>
        <span className="block text-sm">{candidate.effective_date || "—"}</span>
      </span>
      <span>
        <span className="block text-xs font-medium text-muted-foreground">
          Effective end date
        </span>
        <span className="block text-sm">
          {candidate.effective_end_date || "—"}
        </span>
      </span>
      <span>
        <span className="block text-xs font-medium text-muted-foreground">
          APN
        </span>
        <span className="block text-sm">{candidate.property_apn || "—"}</span>
      </span>
      <span className="sm:col-span-2 lg:col-span-3">
        <span className="block text-xs font-medium text-muted-foreground">
          Property
        </span>
        <span className="block text-sm">{formatPropertyLine(candidate)}</span>
      </span>
      <span>
        <span className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground">
          OMT
          <PartyRoleInfo party="OMT" />
        </span>
        <span className="block text-sm">
          {formatLicensePartyDisplay(candidate.omt_display)}
        </span>
      </span>
      <span>
        <span className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground">
          AMS
          <PartyRoleInfo party="AMS" />
        </span>
        <span className="block text-sm">
          {formatLicensePartyDisplay(candidate.ams_display)}
        </span>
      </span>
    </button>
  );
}

function DetailsTable({
  rows,
  columns,
}: {
  rows: Array<Record<string, string | number | null>>;
  columns: readonly string[];
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No rows returned.</p>;
  }

  return (
    <div className="h-full overflow-auto rounded-md border">
      <table className="w-full min-w-max text-sm">
        <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                className="px-3 py-2 text-left font-medium whitespace-nowrap"
              >
                {formatAttomFieldLabel(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={columns.map((column) => String(row[column] ?? "")).join("|")}
              className="border-t"
            >
              {columns.map((column) => (
                <td
                  key={column}
                  className="px-3 py-2 align-top whitespace-nowrap"
                >
                  {formatAttomFieldValue(row[column])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AttomForeclosureDetailsDialog({
  open,
  onOpenChange,
  caseNumber,
  jurisdictionFips,
  caseNumberUrlTemplate,
  caseNumberCopyToClipboard = false,
  caseNumberHttpMethod,
  caseNumberValueExtractRegex,
  caseNumberRequestFields,
  caseNumberBrowserHelperConfig,
  caseNumberBlankAnswerUrl,
  effectiveCaseNumber = null,
  isCaseNumberOverridden = false,
  systemStartDate = null,
  effectiveStartDate = null,
  isStartDateOverridden = false,
  portal,
  jurisdictionId,
  countyFips,
  caseApn,
  propertyAddressFull,
  propertyAddressCity,
  propertyAddressState,
  propertyAddressZip,
  latitude = null,
  longitude = null,
  caseSummary = null,
  reviewStatus = null,
  reviewComment = null,
  reviewUpdatedAt = null,
  reviewUpdatedByDisplay = null,
  suggestedReviewStatus = null,
  suggestedReviewComment = null,
  omtPartyId = null,
  omtDisplay = null,
  amsPartyId = null,
  amsDisplay = null,
  registrantAccountId = null,
  registrantDisplay = null,
  isOmtOverridden = false,
  isAmsOverridden = false,
  isRegistrantOverridden = false,
  unregisteredPropertiesPath = "/government/unregistered-properties",
  onLinked,
  onNext,
  hasNext = false,
  onPrevious,
  hasPrevious = false,
  elevated = false,
}: AttomForeclosureDetailsDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [linkingLicenseId, setLinkingLicenseId] = useState<string | null>(null);
  const [linkingUnregisteredId, setLinkingUnregisteredId] = useState<
    string | null
  >(null);
  const [detailsLicenseId, setDetailsLicenseId] = useState<string | null>(null);
  const [createUnregisteredOpen, setCreateUnregisteredOpen] = useState(false);
  const [suggestedPartyRow, setSuggestedPartyRow] =
    useState<GovernmentForeclosureCaseListItem | null>(null);
  const [draftReviewStatus, setDraftReviewStatus] = useState<
    ForeclosureCaseReviewStatus | "unset"
  >(reviewStatus ?? "unset");
  const [draftComment, setDraftComment] = useState(reviewComment ?? "");
  const {
    size: dialogSize,
    onResizePointerDown,
    onResizeKeyDown,
  } = useForeclosureDetailsDialogSize();

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "attom-foreclosure-case-details",
      caseNumber,
      jurisdictionFips ?? null,
    ],
    queryFn: () => getAttomForeclosureCaseDetails(caseNumber, jurisdictionFips),
    enabled: open && caseNumber.length > 0,
    staleTime: 0,
  });

  const reviewHistoryQueryKey = [
    "foreclosure-case-review-history",
    portal,
    countyFips,
    caseNumber,
  ] as const;
  const { data: reviewHistory = [] } = useQuery({
    queryKey: reviewHistoryQueryKey,
    queryFn: () => {
      if (!portal || !countyFips) {
        throw new Error("portal and countyFips are required to load history");
      }
      return getForeclosureCaseReviewHistory(portal, {
        countyFips,
        caseNumber,
      });
    },
    enabled:
      open && Boolean(portal) && Boolean(countyFips) && caseNumber.length > 0,
    staleTime: 0,
  });

  const initialCaseSummary = useMemo(() => {
    const fromProps: Record<string, string | number | null> = {
      ...(caseSummary ?? {}),
    };
    if (caseNumber) fromProps.case_number = caseNumber;
    if (countyFips) fromProps.county_fips = countyFips;
    if (caseApn) fromProps.apn = caseApn;
    if (propertyAddressFull)
      fromProps.property_address_full = propertyAddressFull;
    if (propertyAddressCity)
      fromProps.property_address_city = propertyAddressCity;
    if (propertyAddressState) {
      fromProps.property_address_state = propertyAddressState;
    }
    if (propertyAddressZip) fromProps.property_address_zip = propertyAddressZip;
    return Object.fromEntries(
      Object.entries(fromProps).filter(
        ([, value]) => value != null && value !== "",
      ),
    );
  }, [
    caseNumber,
    countyFips,
    caseApn,
    propertyAddressFull,
    propertyAddressCity,
    propertyAddressState,
    propertyAddressZip,
    caseSummary,
  ]);

  const displayCase = useMemo(() => {
    const base = { ...(data?.case ?? initialCaseSummary) };
    if (caseNumber) base.case_number = caseNumber;
    // Keep start date in the summary so the details pane can edit it even
    // when ATTOM loaded a blank day.
    if (portal) {
      const shownStart =
        effectiveStartDate ||
        (typeof base.start_date === "string" ? base.start_date : null) ||
        systemStartDate;
      base.start_date = shownStart || "—";
    }
    return base;
  }, [
    data?.case,
    initialCaseSummary,
    caseNumber,
    portal,
    effectiveStartDate,
    systemStartDate,
  ]);
  const hasDisplayCase = Object.keys(displayCase).length > 0;
  const {
    left: caseSummaryLeft,
    middle: caseSummaryMiddle,
    date: caseSummaryDate,
  } = useMemo(() => partitionCaseSummaryEntries(displayCase), [displayCase]);

  const licenseMatchEnabled =
    open &&
    caseNumber.length > 0 &&
    Boolean(portal) &&
    (portal !== "operator" || Boolean(jurisdictionId));

  const {
    data: licenseContext,
    error: licenseError,
    isLoading: licenseLoading,
    refetch: refetchLicenseContext,
  } = useQuery({
    queryKey: [
      "foreclosure-license-matches",
      portal,
      caseNumber,
      jurisdictionId ?? null,
      caseApn ?? null,
      propertyAddressFull ?? null,
      propertyAddressCity ?? null,
      propertyAddressState ?? null,
      propertyAddressZip ?? null,
    ],
    queryFn: () => {
      if (!portal) {
        throw new Error("portal is required for license matches");
      }
      return getForeclosureLicenseMatches(portal, {
        caseNumber,
        apn: caseApn,
        propertyAddressFull,
        propertyAddressCity,
        propertyAddressState,
        propertyAddressZip,
        jurisdictionId,
      });
    },
    enabled: licenseMatchEnabled,
    staleTime: 0,
  });

  const unregisteredMatchEnabled =
    open &&
    Boolean(portal) &&
    Boolean(jurisdictionId) &&
    (Boolean(caseNumber) ||
      Boolean(caseApn) ||
      Boolean(propertyAddressFull) ||
      Boolean(propertyAddressCity));

  const {
    data: unregisteredContext,
    isLoading: unregisteredLoading,
    refetch: refetchUnregisteredContext,
  } = useQuery({
    queryKey: [
      "foreclosure-unregistered-match",
      portal,
      jurisdictionId ?? null,
      caseNumber,
      caseApn ?? null,
      propertyAddressFull ?? null,
      propertyAddressCity ?? null,
      propertyAddressState ?? null,
      propertyAddressZip ?? null,
    ],
    queryFn: () => {
      if (!portal) {
        throw new Error("portal is required for unregistered match");
      }
      return getForeclosureUnregisteredMatch(portal, {
        caseNumber,
        apn: caseApn,
        propertyAddressFull,
        propertyAddressCity,
        propertyAddressState,
        propertyAddressZip,
        jurisdictionId,
      });
    },
    enabled: unregisteredMatchEnabled,
    staleTime: 0,
  });

  const governmentOrdinanceQuery = useQuery({
    ...getGovernmentLicenseTypeConfigurationsOptions(),
    enabled: open && portal === "government",
  });
  const governmentJurisdictionQuery = useQuery({
    ...getGovernmentJurisdictionOptions(),
    enabled: open && portal === "government",
  });
  const operatorJurisdictionsQuery = useQuery({
    ...getJurisdictionsOptions({ query: { include_deleted: false } }),
    enabled: open && portal === "operator" && Boolean(jurisdictionId),
  });

  const ordinanceJurisdictionName = useMemo(() => {
    const fromCase = displayCase.jurisdiction_name;
    if (typeof fromCase === "string" && fromCase.trim()) {
      return fromCase.trim();
    }
    if (portal === "operator") {
      const match = operatorJurisdictionsQuery.data?.jurisdictions.find(
        (jurisdiction) => jurisdiction.id === jurisdictionId,
      );
      return match?.name ?? "";
    }
    return governmentJurisdictionQuery.data?.name ?? "";
  }, [
    displayCase.jurisdiction_name,
    governmentJurisdictionQuery.data?.name,
    jurisdictionId,
    operatorJurisdictionsQuery.data?.jurisdictions,
    portal,
  ]);

  const ordinanceSummaries = useMemo(() => {
    if (portal === "operator") {
      const configs =
        operatorJurisdictionsQuery.data?.jurisdictions.find(
          (jurisdiction) => jurisdiction.id === jurisdictionId,
        )?.license_type_configurations ?? [];
      return buildForeclosureOrdinanceSummaries(
        configs,
        ordinanceJurisdictionName,
      );
    }
    return buildForeclosureOrdinanceSummaries(
      governmentOrdinanceQuery.data ?? [],
      ordinanceJurisdictionName,
    );
  }, [
    governmentOrdinanceQuery.data,
    jurisdictionId,
    operatorJurisdictionsQuery.data?.jurisdictions,
    ordinanceJurisdictionName,
    portal,
  ]);

  const ordinanceSummaryLoading =
    portal === "government"
      ? governmentOrdinanceQuery.isLoading ||
        governmentJurisdictionQuery.isLoading
      : portal === "operator"
        ? operatorJurisdictionsQuery.isLoading
        : false;

  const questionsJurisdictionId =
    jurisdictionId || governmentJurisdictionQuery.data?.id || "";
  const requirementLicenseTypeId = ordinanceSummaries[0]?.licenseTypeId ?? "";
  const requirementQuestionsQuery = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: requirementLicenseTypeId || "none",
        jurisdiction_id: questionsJurisdictionId || "none",
      },
    }),
    enabled:
      open &&
      Boolean(portal) &&
      Boolean(questionsJurisdictionId) &&
      Boolean(requirementLicenseTypeId),
  });
  const requirementQuestionsByLicenseTypeId: Record<
    string,
    ReturnType<typeof registrationRequirementQuestions>
  > = {};
  const loadedRequirementQuestions = registrationRequirementQuestions(
    requirementQuestionsQuery.data?.questions ?? [],
  );
  for (const summary of ordinanceSummaries) {
    requirementQuestionsByLicenseTypeId[summary.licenseTypeId] =
      summary.licenseTypeId === requirementLicenseTypeId
        ? loadedRequirementQuestions
        : [];
  }

  const matchedUnregistered: ForeclosureUnregisteredMatch | null =
    unregisteredContext?.matched ?? null;
  const matchedRegistrantDisplay = matchedUnregistered
    ? registrantDisplayName({
        name: matchedUnregistered.registrant_name,
        company_name: matchedUnregistered.registrant_company_name,
      })
    : null;
  const foreclosureLicenseTypes =
    unregisteredContext?.foreclosure_license_types ?? [];
  const unregisteredCandidates = unregisteredContext?.candidates ?? [];
  const canCreateUnregistered =
    Boolean(jurisdictionId) &&
    foreclosureLicenseTypes.length > 0 &&
    !matchedUnregistered &&
    !unregisteredLoading;

  const partyListRow = useMemo((): GovernmentForeclosureCaseListItem | null => {
    if (!countyFips || !portal) return null;
    const baseRow: GovernmentForeclosureCaseListItem = {
      case_number: caseNumber,
      case_key: null,
      county_fips: countyFips,
      jurisdiction_fips: jurisdictionFips ?? countyFips,
      jurisdiction_fips_display: null,
      jurisdiction_name: null,
      jurisdiction_id: jurisdictionId ?? null,
      property_address_full: propertyAddressFull ?? null,
      property_address_city: propertyAddressCity ?? null,
      property_address_state: propertyAddressState ?? null,
      property_address_zip: propertyAddressZip ?? null,
      apn: caseApn ?? null,
      latest_document_date: null,
      license_id: null,
      license_display_id: null,
      review_status: reviewStatus,
      review_comment: reviewComment,
      suggested_review_status: suggestedReviewStatus,
      suggested_review_comment: suggestedReviewComment,
      omt_party_id: omtPartyId,
      omt_display: omtDisplay,
      ams_party_id: amsPartyId,
      ams_display: amsDisplay,
      registrant_account_id: registrantAccountId,
      registrant_display: registrantDisplay,
      is_omt_overridden: isOmtOverridden,
      is_ams_overridden: isAmsOverridden,
      is_registrant_overridden: isRegistrantOverridden,
      effective_case_number: effectiveCaseNumber ?? caseNumber,
      is_case_number_overridden: isCaseNumberOverridden,
      start_date: systemStartDate,
      effective_start_date: effectiveStartDate ?? systemStartDate,
      is_start_date_overridden: isStartDateOverridden,
      case_number_url_template: caseNumberUrlTemplate ?? null,
      case_number_copy_to_clipboard: caseNumberCopyToClipboard,
      case_number_http_method: caseNumberHttpMethod ?? null,
      case_number_value_extract_regex: caseNumberValueExtractRegex ?? null,
      case_number_request_fields: caseNumberRequestFields ?? null,
      case_number_browser_helper_config: caseNumberBrowserHelperConfig ?? null,
      case_number_blank_answer_url: caseNumberBlankAnswerUrl ?? null,
    };
    return suggestedPartyRow ? { ...baseRow, ...suggestedPartyRow } : baseRow;
  }, [
    amsDisplay,
    amsPartyId,
    caseApn,
    caseNumber,
    countyFips,
    isAmsOverridden,
    isOmtOverridden,
    isRegistrantOverridden,
    jurisdictionFips,
    jurisdictionId,
    omtDisplay,
    omtPartyId,
    portal,
    propertyAddressCity,
    propertyAddressFull,
    propertyAddressState,
    propertyAddressZip,
    registrantAccountId,
    registrantDisplay,
    reviewComment,
    reviewStatus,
    suggestedPartyRow,
    suggestedReviewComment,
    suggestedReviewStatus,
    effectiveCaseNumber,
    isCaseNumberOverridden,
    systemStartDate,
    effectiveStartDate,
    isStartDateOverridden,
    caseNumberUrlTemplate,
    caseNumberCopyToClipboard,
    caseNumberHttpMethod,
    caseNumberValueExtractRegex,
    caseNumberRequestFields,
    caseNumberBrowserHelperConfig,
    caseNumberBlankAnswerUrl,
  ]);

  const refreshCaseListsInBackground = () => {
    void invalidateForeclosureCaseListRowQueries(queryClient);
    void invalidateForeclosureCaseChartQueries(queryClient);
  };

  const linkMutation = useMutation({
    mutationFn: (vars: {
      licenseId: string;
      caseNumber: string;
      jurisdictionId?: string | null;
      countyFips?: string | null;
    }) => {
      if (!portal) {
        throw new Error("portal is required to link a license");
      }
      return linkForeclosureCaseToLicense(portal, {
        caseNumber: vars.caseNumber,
        licenseId: vars.licenseId,
        jurisdictionId: vars.jurisdictionId,
        countyFips: vars.countyFips,
      });
    },
    onMutate: (vars) => {
      setLinkingLicenseId(vars.licenseId);
    },
    onSuccess: (_linked, vars) => {
      toast({
        title: "Case number applied",
        description: `Linked ${vars.caseNumber} to the selected license.`,
        variant: "secondary",
      });
      refreshCaseListsInBackground();
      void queryClient.invalidateQueries({ queryKey: reviewHistoryQueryKey });
      onLinked?.();
      if (vars.caseNumber !== caseNumber) {
        return;
      }
      setDraftReviewStatus("assigned");
      // Do not await: keeping the mutation pending through this refetch
      // greys out Apply on the next case after Next/Previous.
      void refetchLicenseContext();
    },
    onError: (linkError) => {
      toast({
        title: "Failed to apply case number",
        description: getQueryErrorMessage(linkError),
        variant: "destructive",
      });
    },
    onSettled: (_linked, _error, vars) => {
      if (vars.caseNumber === caseNumber) {
        setLinkingLicenseId(null);
      }
    },
  });

  const linkUnregisteredMutation = useMutation({
    mutationFn: (vars: { unregisteredId: string }) => {
      if (!portal) {
        throw new Error("portal is required to link an unregistered property");
      }
      return linkForeclosureCaseToUnregistered(portal, {
        caseNumber,
        unregisteredId: vars.unregisteredId,
        jurisdictionId,
        countyFips,
      });
    },
    onMutate: (vars) => {
      setLinkingUnregisteredId(vars.unregisteredId);
    },
    onSuccess: () => {
      toast({
        title: "Case number applied",
        description: `Linked ${caseNumber} to the selected unregistered property.`,
        variant: "secondary",
      });
      setDraftReviewStatus("unregistered_property_created");
      refreshCaseListsInBackground();
      void queryClient.invalidateQueries({ queryKey: reviewHistoryQueryKey });
      void refetchUnregisteredContext();
    },
    onError: (linkError) => {
      toast({
        title: "Failed to apply case number",
        description: getQueryErrorMessage(linkError),
        variant: "destructive",
      });
    },
    onSettled: () => {
      setLinkingUnregisteredId(null);
    },
  });

  const reviewMutation = useMutation({
    mutationFn: () => {
      if (!portal || !countyFips) {
        throw new Error("portal and countyFips are required to save review");
      }
      return updateForeclosureCaseReview(portal, {
        countyFips,
        caseNumber,
        reviewStatus: draftReviewStatus === "unset" ? null : draftReviewStatus,
        reviewComment: draftComment.trim() ? draftComment.trim() : null,
      });
    },
    onSuccess: (review) => {
      applyForeclosureCaseReviewToCachedLists(queryClient, review);
      void queryClient.invalidateQueries({ queryKey: reviewHistoryQueryKey });
      void invalidateForeclosureCaseChartQueries(queryClient);
      toast({
        title: "Review saved",
        description: `Updated review flags for ${caseNumber}.`,
        variant: "secondary",
      });
    },
    onError: (reviewError) => {
      toast({
        title: "Failed to save review",
        description: getQueryErrorMessage(reviewError),
        variant: "destructive",
      });
    },
  });

  const suggestPartiesMutation = useMutation({
    mutationFn: () => {
      if (!portal || !countyFips) {
        throw new Error(
          "portal and countyFips are required to suggest parties",
        );
      }
      return suggestForeclosureCaseParties(portal, {
        countyFips,
        caseNumber,
        jurisdictionId,
      });
    },
    onSuccess: (row) => {
      setSuggestedPartyRow(row);
      refreshCaseListsInBackground();
      toast({
        title: "Suggestions updated",
        description:
          row.suggested_omt_party_id ||
          row.suggested_ams_party_id ||
          row.suggested_registrant_account_id
            ? `Recomputed OMT, AMS, and registrant suggestions for ${caseNumber}.`
            : `No OMT, AMS, or registrant match was found for ${caseNumber}.`,
        variant: "secondary",
      });
    },
    onError: (suggestError) => {
      toast({
        title: "Failed to suggest parties",
        description: getQueryErrorMessage(suggestError),
        variant: "destructive",
      });
    },
  });

  const applySuggestedReviewToDrafts = (
    row: GovernmentForeclosureCaseListItem,
  ) => {
    if (row.suggested_review_status) {
      setDraftReviewStatus(row.suggested_review_status);
    }
    if (row.suggested_review_comment) {
      setDraftComment(row.suggested_review_comment);
    }
  };

  const suggestReviewMutation = useMutation({
    mutationFn: () => {
      if (!portal || !countyFips) {
        throw new Error("portal and countyFips are required to suggest review");
      }
      return suggestForeclosureCaseReview(portal, {
        countyFips,
        caseNumber,
        jurisdictionId,
      });
    },
    onSuccess: (row) => {
      setSuggestedPartyRow(row);
      applySuggestedReviewToDrafts(row);
      refreshCaseListsInBackground();
      toast({
        title: "Review suggestion updated",
        description: row.suggested_review_status
          ? `Suggested ${FORECLOSURE_CASE_REVIEW_STATUS_LABELS[row.suggested_review_status]} for ${caseNumber}.`
          : `No review-status match was found for ${caseNumber}.`,
        variant: "secondary",
      });
    },
    onError: (suggestError) => {
      toast({
        title: "Failed to suggest review",
        description: getQueryErrorMessage(suggestError),
        variant: "destructive",
      });
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset draft when Next/Previous changes case
  useEffect(() => {
    if (!open) return;
    setDraftReviewStatus(reviewStatus ?? "unset");
    setDraftComment(reviewComment ?? "");
    setSuggestedPartyRow(null);
  }, [open, caseNumber, countyFips, reviewStatus, reviewComment]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset saving/linking UI only when navigating to another case
  useEffect(() => {
    reviewMutation.reset();
    suggestPartiesMutation.reset();
    suggestReviewMutation.reset();
    setLinkingLicenseId(null);
  }, [caseNumber, countyFips]);

  const renderCaseSummaryField = (
    key: string,
    value: string | number | null,
  ) => {
    if (portal && partyListRow && key === "case_number") {
      return (
        <div className="min-w-0" key={key}>
          <dt className="break-words text-xs font-medium text-muted-foreground">
            Case Number
          </dt>
          <dd className="text-sm">
            <ForeclosureCaseNumberEditCell row={partyListRow} portal={portal} />
          </dd>
        </div>
      );
    }
    if (portal && partyListRow && key === "start_date") {
      return (
        <div className="min-w-0" key={key}>
          <dt className="break-words text-xs font-medium text-muted-foreground">
            Start Date
          </dt>
          <dd className="text-sm">
            <ForeclosureCaseStartDateEditCell
              row={partyListRow}
              portal={portal}
            />
          </dd>
        </div>
      );
    }
    return <CaseSummaryField key={key} fieldKey={key} value={value} />;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          elevated={elevated}
          className="flex max-h-none w-auto max-w-none flex-col gap-0 overflow-hidden p-6 sm:max-w-none"
          style={{
            width: dialogSize.width,
            height: dialogSize.height,
            maxWidth: dialogSize.width,
            maxHeight: dialogSize.height,
          }}
        >
          <DialogHeader className="shrink-0 pb-4">
            <div className="flex items-start justify-between gap-4 pr-8">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <DialogTitle>ATTOM foreclosure details</DialogTitle>
                  {portal ? (
                    <ForeclosureCaseReviewStatusBadge
                      status={
                        draftReviewStatus === "unset" ? null : draftReviewStatus
                      }
                      labelPrefix="Review status"
                    />
                  ) : null}
                </div>
                <DialogDescription className="sr-only">
                  Details for foreclosure case {caseNumber}
                </DialogDescription>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {onPrevious || onNext ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!hasPrevious}
                      onClick={onPrevious}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!hasNext}
                      onClick={onNext}
                    >
                      Next
                    </Button>
                  </>
                ) : null}
                <LiveLinkButton
                  value={effectiveCaseNumber?.trim() || caseNumber}
                  urlTemplate={caseNumberUrlTemplate}
                  copyValueToClipboard={caseNumberCopyToClipboard}
                  httpMethod={caseNumberHttpMethod}
                  valueExtractRegex={caseNumberValueExtractRegex}
                  requestFields={caseNumberRequestFields}
                  browserHelperConfig={caseNumberBrowserHelperConfig}
                  blankAnswerUrl={caseNumberBlankAnswerUrl}
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                >
                  Proceed to Case Records
                </LiveLinkButton>
              </div>
            </div>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
            {portal ? (
              <ForeclosureOrdinanceSummarySection
                isLoading={ordinanceSummaryLoading}
                summaries={ordinanceSummaries}
                fallbackJurisdictionName={ordinanceJurisdictionName}
                requirementQuestionsByLicenseTypeId={
                  requirementQuestionsByLicenseTypeId
                }
              />
            ) : null}
            <section className="shrink-0 space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Case summary
              </h3>
              {hasDisplayCase ? (
                <div
                  className={cn(
                    FORECLOSURE_DETAIL_TILE_CLASS,
                    "grid content-start gap-3 lg:grid-cols-3",
                  )}
                >
                  <dl className="grid content-start gap-3">
                    {caseSummaryLeft.map(([key, value]) =>
                      renderCaseSummaryField(key, value),
                    )}
                  </dl>
                  <dl className="grid content-start gap-3">
                    {caseSummaryMiddle.map(([key, value]) =>
                      renderCaseSummaryField(key, value),
                    )}
                  </dl>
                  {caseSummaryDate.length > 0 ? (
                    <dl className="grid content-start gap-3">
                      {caseSummaryDate.map(([key, value]) =>
                        renderCaseSummaryField(key, value),
                      )}
                    </dl>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No matching case found in foreclosure_cases.
                </p>
              )}
            </section>

            {portal && partyListRow ? (
              <section className="shrink-0 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    OMT / AMS / Registrant
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={
                      suggestPartiesMutation.isPending ||
                      (portal === "operator" && !jurisdictionId)
                    }
                    onClick={() => suggestPartiesMutation.mutate()}
                  >
                    {suggestPartiesMutation.isPending
                      ? "Suggesting…"
                      : "Suggest"}
                  </Button>
                </div>
                <dl className="grid gap-3 rounded-md border p-4 sm:grid-cols-3">
                  <div>
                    <dt className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground">
                      OMT
                      <PartyRoleInfo party="OMT" />
                    </dt>
                    <dd className="text-sm">
                      <ForeclosureCasePartyEditCell
                        row={partyListRow}
                        field="omt"
                        portal={portal}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground">
                      AMS
                      <PartyRoleInfo party="AMS" />
                    </dt>
                    <dd className="text-sm">
                      <ForeclosureCasePartyEditCell
                        row={partyListRow}
                        field="ams"
                        portal={portal}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-muted-foreground">
                      Registrant
                    </dt>
                    <dd className="text-sm">
                      <ForeclosureCasePartyEditCell
                        row={partyListRow}
                        field="registrant"
                        portal={portal}
                      />
                    </dd>
                  </div>
                </dl>
              </section>
            ) : null}

            {portal ? (
              <section className="shrink-0 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Case review
                    </h3>
                    <ForeclosureCaseReviewStatusBadge
                      status={
                        draftReviewStatus === "unset" ? null : draftReviewStatus
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={
                      suggestReviewMutation.isPending ||
                      (portal === "operator" && !jurisdictionId)
                    }
                    onClick={() => suggestReviewMutation.mutate()}
                  >
                    {suggestReviewMutation.isPending
                      ? "Suggesting…"
                      : "Suggest review"}
                  </Button>
                </div>
                <div className="space-y-4 rounded-md border p-4">
                  {licenseLoading ? (
                    <p className="text-sm text-muted-foreground">
                      Looking up matching licenses…
                    </p>
                  ) : null}
                  {!licenseLoading && licenseError ? (
                    <ErrorComponent
                      title="Failed to load license matches"
                      message={getQueryErrorMessage(licenseError)}
                      action={{
                        label: "Retry",
                        onClick: () => void refetchLicenseContext(),
                      }}
                    />
                  ) : null}
                  {!licenseLoading &&
                  !licenseError &&
                  licenseContext?.linked_license ? (
                    <LicenseDetailsCard
                      candidate={licenseContext.linked_license}
                      onOpen={() =>
                        setDetailsLicenseId(
                          licenseContext.linked_license?.license_id ?? null,
                        )
                      }
                    />
                  ) : null}
                  {/* Once an unregistered property is attached to the case,
                      probable license matches are no longer actionable. */}
                  {!licenseLoading &&
                  !licenseError &&
                  licenseContext &&
                  !licenseContext.linked_license &&
                  !unregisteredLoading &&
                  !matchedUnregistered ? (
                    <div className="space-y-3">
                      {licenseContext.candidates.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No foreclosure licenses had a positive APN or address
                          match score for this case.
                        </p>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-muted-foreground">
                            Probable matching licenses
                          </p>
                          <div className="max-h-56 overflow-auto rounded-md border">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium">
                                    License
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Status
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Effective
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    End
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    APN
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Property
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    OMT
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    AMS
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Match
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {licenseContext.candidates.map((candidate) => {
                                  const statusDisplay =
                                    getRegistrationStatusDisplay(
                                      candidate.status,
                                    );
                                  const openLicenseDetails = () =>
                                    setDetailsLicenseId(candidate.license_id);
                                  return (
                                    <tr
                                      key={candidate.license_id}
                                      className="cursor-pointer border-t transition-colors hover:bg-muted/50"
                                      onClick={openLicenseDetails}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          openLicenseDetails();
                                        }
                                      }}
                                    >
                                      <td className="px-3 py-2 align-top whitespace-nowrap">
                                        <span className="text-primary underline-offset-4 hover:underline">
                                          {candidate.license_display_id}
                                        </span>
                                        {candidate.is_deleted ? (
                                          <div className="text-xs text-muted-foreground">
                                            inactive
                                          </div>
                                        ) : null}
                                      </td>
                                      <td className="px-3 py-2 align-top">
                                        <Badge variant={statusDisplay.variant}>
                                          {statusDisplay.label}
                                        </Badge>
                                      </td>
                                      <td className="px-3 py-2 align-top whitespace-nowrap">
                                        {candidate.effective_date || "—"}
                                      </td>
                                      <td className="px-3 py-2 align-top whitespace-nowrap">
                                        {candidate.effective_end_date || "—"}
                                      </td>
                                      <td className="px-3 py-2 align-top whitespace-nowrap">
                                        {candidate.property_apn || "—"}
                                      </td>
                                      <td className="px-3 py-2 align-top">
                                        {formatPropertyLine(candidate)}
                                      </td>
                                      <td className="px-3 py-2 align-top">
                                        {formatLicensePartyDisplay(
                                          candidate.omt_display,
                                        )}
                                      </td>
                                      <td className="px-3 py-2 align-top">
                                        {formatLicensePartyDisplay(
                                          candidate.ams_display,
                                        )}
                                      </td>
                                      <td className="px-3 py-2 align-top whitespace-nowrap">
                                        {Math.round(candidate.match_score)}% ·{" "}
                                        {formatMatchReasons(
                                          candidate.match_reasons,
                                        )}
                                        {candidate.existing_case_number ? (
                                          <div className="text-xs text-muted-foreground">
                                            Has case{" "}
                                            {candidate.existing_case_number}
                                          </div>
                                        ) : null}
                                      </td>
                                      <td
                                        className="px-3 py-2 align-top"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                      >
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={linkingLicenseId != null}
                                          onClick={() =>
                                            linkMutation.mutate({
                                              licenseId: candidate.license_id,
                                              caseNumber,
                                              jurisdictionId,
                                              countyFips,
                                            })
                                          }
                                        >
                                          {linkingLicenseId ===
                                          candidate.license_id
                                            ? "Applying…"
                                            : "Apply case #"}
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  ) : null}

                  {!unregisteredLoading &&
                  !matchedUnregistered &&
                  unregisteredCandidates.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-muted-foreground">
                        Probable matching unregistered properties
                      </p>
                      <div className="max-h-56 overflow-auto rounded-md border">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium">
                                Property
                              </th>
                              <th className="px-3 py-2 text-left font-medium">
                                APN
                              </th>
                              <th className="px-3 py-2 text-left font-medium">
                                Effective
                              </th>
                              <th className="px-3 py-2 text-left font-medium">
                                Match
                              </th>
                              <th className="px-3 py-2 text-left font-medium">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {unregisteredCandidates.map((candidate) => (
                              <tr
                                key={candidate.unregistered_id}
                                className="border-t"
                              >
                                <td className="px-3 py-2 align-top">
                                  {[
                                    candidate.property_address1,
                                    candidate.property_city,
                                    candidate.property_state,
                                    candidate.property_zipcode,
                                  ]
                                    .filter(Boolean)
                                    .join(", ") || "—"}
                                </td>
                                <td className="px-3 py-2 align-top whitespace-nowrap">
                                  {candidate.property_apn || "—"}
                                </td>
                                <td className="px-3 py-2 align-top whitespace-nowrap">
                                  {candidate.effective_date || "—"}
                                </td>
                                <td className="px-3 py-2 align-top whitespace-nowrap">
                                  {Math.round(candidate.match_score)}% ·{" "}
                                  {formatMatchReasons(candidate.match_reasons)}
                                  {candidate.existing_case_number ? (
                                    <div className="text-xs text-muted-foreground">
                                      Has case {candidate.existing_case_number}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="px-3 py-2 align-top">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={linkingUnregisteredId != null}
                                    onClick={() =>
                                      linkUnregisteredMutation.mutate({
                                        unregisteredId:
                                          candidate.unregistered_id,
                                      })
                                    }
                                  >
                                    {linkingUnregisteredId ===
                                    candidate.unregistered_id
                                      ? "Applying…"
                                      : "Apply case #"}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                  {unregisteredLoading ? (
                    <p className="text-sm text-muted-foreground">
                      Looking up matching unregistered properties…
                    </p>
                  ) : null}
                  {!unregisteredLoading && matchedUnregistered ? (
                    <div
                      className="cursor-pointer space-y-2 rounded-md border bg-muted/20 p-3 transition-colors hover:bg-muted/50"
                      onClick={() =>
                        void navigate({
                          to: unregisteredPropertiesPath,
                          search: {
                            licenseId: undefined,
                            unregisteredId: matchedUnregistered.unregistered_id,
                          },
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          void navigate({
                            to: unregisteredPropertiesPath,
                            search: {
                              licenseId: undefined,
                              unregisteredId:
                                matchedUnregistered.unregistered_id,
                            },
                          });
                        }
                      }}
                    >
                      <p className="text-sm font-medium">
                        Unregistered property
                      </p>
                      <dl className="grid gap-2 text-sm sm:grid-cols-2">
                        <div>
                          <dt className="text-xs text-muted-foreground">
                            License type
                          </dt>
                          <dd>
                            {matchedUnregistered.license_type_name || "—"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-muted-foreground">
                            Effective date
                          </dt>
                          <dd>{matchedUnregistered.effective_date || "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-muted-foreground">APN</dt>
                          <dd>{matchedUnregistered.property_apn || "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-muted-foreground">
                            Property
                          </dt>
                          <dd>
                            {[
                              matchedUnregistered.property_address1,
                              matchedUnregistered.property_city,
                              matchedUnregistered.property_state,
                              matchedUnregistered.property_zipcode,
                            ]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </dd>
                        </div>
                        <div className="sm:col-span-2">
                          <dt className="text-xs text-muted-foreground">
                            Linked registrant
                          </dt>
                          <dd>
                            {matchedUnregistered.registrant_email ? (
                              <>
                                {matchedRegistrantDisplay ??
                                  matchedUnregistered.registrant_email}
                                {matchedRegistrantDisplay && (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    — {matchedUnregistered.registrant_email}
                                  </span>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </dd>
                        </div>
                      </dl>
                      <Link
                        to={unregisteredPropertiesPath}
                        search={{
                          licenseId: undefined,
                          unregisteredId: matchedUnregistered.unregistered_id,
                        }}
                        className="text-sm text-primary underline-offset-4 hover:underline"
                      >
                        Open unregistered property
                      </Link>
                    </div>
                  ) : null}

                  {countyFips ? (
                    <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-stretch">
                      <div className="grid min-w-0 flex-[2] gap-2">
                        <Label htmlFor="foreclosure-review-comment">
                          Review comment
                        </Label>
                        <Textarea
                          id="foreclosure-review-comment"
                          className="min-h-20 flex-1 resize-y"
                          value={draftComment}
                          onChange={(event) =>
                            setDraftComment(event.target.value)
                          }
                          placeholder="Notes from case review…"
                          rows={3}
                        />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-3">
                        <div className="grid gap-2">
                          <Label htmlFor="foreclosure-review-status">
                            Review status
                          </Label>
                          <select
                            id="foreclosure-review-status"
                            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                            value={draftReviewStatus}
                            onChange={(event) =>
                              setDraftReviewStatus(
                                event.target.value as
                                  | ForeclosureCaseReviewStatus
                                  | "unset",
                              )
                            }
                          >
                            <option value="unset">Not set</option>
                            {FORECLOSURE_CASE_REVIEW_STATUS_OPTIONS.map(
                              (status) => (
                                <option key={status} value={status}>
                                  {
                                    FORECLOSURE_CASE_REVIEW_STATUS_LABELS[
                                      status
                                    ]
                                  }
                                </option>
                              ),
                            )}
                          </select>
                          {partyListRow?.suggested_review_status ? (
                            <p className="text-xs text-muted-foreground">
                              Suggested:{" "}
                              {
                                FORECLOSURE_CASE_REVIEW_STATUS_LABELS[
                                  partyListRow.suggested_review_status
                                ]
                              }
                              {partyListRow.suggested_review_comment
                                ? ` — ${partyListRow.suggested_review_comment}`
                                : ""}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap justify-end gap-2 self-end">
                          <Button
                            size="sm"
                            disabled={!canCreateUnregistered}
                            title={
                              matchedUnregistered
                                ? "An unregistered property is already attached by case number"
                                : foreclosureLicenseTypes.length === 0
                                  ? "No foreclosure license type is configured for this jurisdiction"
                                  : undefined
                            }
                            onClick={() => setCreateUnregisteredOpen(true)}
                          >
                            Create Unregistered Property
                          </Button>
                          <Button
                            size="sm"
                            disabled={reviewMutation.isPending}
                            onClick={() => reviewMutation.mutate()}
                          >
                            {reviewMutation.isPending
                              ? "Saving…"
                              : "Save review"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            <section className="flex shrink-0 flex-col gap-3 pb-1">
              <h3 className="shrink-0 text-sm font-medium text-muted-foreground">
                Related documents
              </h3>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">
                  Loading documents from Athena…
                </p>
              ) : null}
              {!isLoading && error ? (
                <ErrorComponent
                  title="Failed to load related documents"
                  message={getQueryErrorMessage(error)}
                  action={{
                    label: "Retry",
                    onClick: () => void refetch(),
                  }}
                />
              ) : null}
              {!isLoading && !error && data ? (
                <div className="h-72 overflow-hidden">
                  {isFetching ? (
                    <p className="mb-2 text-sm text-muted-foreground">
                      Refreshing documents…
                    </p>
                  ) : null}
                  <DetailsTable
                    rows={data.documents}
                    columns={DOCUMENT_COLUMNS}
                  />
                </div>
              ) : null}
            </section>

            <section className="shrink-0 space-y-2 pb-1">
              <h3 className="text-sm font-medium text-muted-foreground">
                History
              </h3>
              <div
                className={cn(
                  FORECLOSURE_DETAIL_TILE_CLASS,
                  "max-h-none space-y-3 text-sm",
                )}
              >
                {reviewHistory.length > 0 ? (
                  reviewHistory.map((event, index) => (
                    <HistoryActorRow
                      key={`${event.changed_at}-${event.review_status ?? "cleared"}-${index}`}
                      label={
                        event.review_status
                          ? "Review status set"
                          : "Review status cleared"
                      }
                      when={event.changed_at}
                      actor={event.changed_by_display}
                      fallbackActor={
                        index === 0 ? reviewUpdatedByDisplay : undefined
                      }
                      extra={
                        [
                          event.review_status
                            ? FORECLOSURE_CASE_REVIEW_STATUS_LABELS[
                                event.review_status
                              ]
                            : null,
                          event.review_comment,
                        ]
                          .filter(Boolean)
                          .join(" — ") || null
                      }
                    />
                  ))
                ) : reviewStatus ||
                  reviewUpdatedAt ||
                  reviewUpdatedByDisplay ||
                  reviewComment ? (
                  <>
                    {reviewStatus ||
                    reviewUpdatedAt ||
                    reviewUpdatedByDisplay ? (
                      <HistoryActorRow
                        label="Review status set"
                        when={reviewUpdatedAt}
                        actor={reviewUpdatedByDisplay}
                        extra={
                          reviewStatus
                            ? FORECLOSURE_CASE_REVIEW_STATUS_LABELS[
                                reviewStatus
                              ]
                            : null
                        }
                      />
                    ) : null}
                    {reviewComment ? (
                      <HistoryActorRow
                        label="Review comment"
                        when={reviewUpdatedAt}
                        actor={reviewUpdatedByDisplay}
                        extra={reviewComment}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No review status has been set yet.
                  </p>
                )}
              </div>
            </section>
          </div>
          <button
            type="button"
            aria-label="Resize dialog"
            title="Drag to resize. Arrow keys adjust width and height."
            className="absolute right-2 bottom-2 z-20 flex size-5 cursor-nwse-resize touch-none items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            onPointerDown={onResizePointerDown}
            onKeyDown={onResizeKeyDown}
          >
            <svg
              viewBox="0 0 12 12"
              aria-hidden="true"
              className="pointer-events-none size-3.5"
            >
              <rect x="7" y="7" width="2" height="2" fill="currentColor" />
              <rect x="3" y="7" width="2" height="2" fill="currentColor" />
              <rect x="7" y="3" width="2" height="2" fill="currentColor" />
            </svg>
          </button>
        </DialogContent>
      </Dialog>
      {portal ? (
        <ForeclosureLicenseDetailsDialog
          key={detailsLicenseId ?? "closed"}
          licenseId={detailsLicenseId}
          portal={portal}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setDetailsLicenseId(null);
          }}
        />
      ) : null}
      {jurisdictionId && portal ? (
        <CreateUnregisteredFromForeclosureDialog
          open={createUnregisteredOpen}
          onOpenChange={setCreateUnregisteredOpen}
          jurisdictionId={jurisdictionId}
          licenseTypes={foreclosureLicenseTypes}
          caseNumber={caseNumber}
          address1={propertyAddressFull?.trim() || ""}
          city={propertyAddressCity?.trim() || ""}
          state={propertyAddressState?.trim() || ""}
          zipcode={propertyAddressZip?.trim() || ""}
          apn={caseApn?.trim() || ""}
          latitude={typeof latitude === "number" ? latitude : null}
          longitude={typeof longitude === "number" ? longitude : null}
          defaultEffectiveDate={
            typeof caseSummary?.start_date === "string"
              ? caseSummary.start_date
              : null
          }
          defaultOwnerName={
            typeof displayCase.borrower_name_owner === "string"
              ? displayCase.borrower_name_owner
              : typeof displayCase.borrower_name === "string"
                ? displayCase.borrower_name
                : null
          }
          defaultRegistrantAccountId={registrantAccountId}
          defaultOmtPartyId={omtPartyId ?? partyListRow?.suggested_omt_party_id}
          defaultAmsPartyId={
            amsPartyId ??
            partyListRow?.suggested_ams_party_id ??
            omtPartyId ??
            partyListRow?.suggested_omt_party_id
          }
          portal={portal}
          onCreated={async (overrides) => {
            setDraftReviewStatus("unregistered_property_created");
            if (portal && countyFips) {
              const review = await updateForeclosureCaseReview(portal, {
                countyFips,
                caseNumber,
                reviewStatus: "unregistered_property_created",
                reviewComment: draftComment.trim() ? draftComment.trim() : null,
                ...overrides,
              });
              applyForeclosureCaseReviewToCachedLists(queryClient, review);
              void invalidateForeclosureCaseChartQueries(queryClient);
            }
            await refetchUnregisteredContext();
            await queryClient.invalidateQueries({
              queryKey: ["unregistered"],
            });
          }}
        />
      ) : null}
    </>
  );
}
